import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n';
import { useAuthStore } from '../store/authStore';
import { isNativePlatform } from '../lib/runtime';
import { autoReleasePlugin } from '../lib/autoReleasePlugin';
import { updateLocationAutomation, getLocationAutomation } from '../api/users';
import AutoReleaseRationaleDialog from './AutoReleaseRationaleDialog';
import analytics from '../lib/analytics';

type ReleaseMethod = 'schedule' | 'share_now' | 'auto';

interface SpotInfo {
  id: string;
  spot_number: string;
  level_label: string;
  level_code: string;
}

interface Props {
  open: boolean;
  spots: SpotInfo[];
  onDismiss: () => void;
}

type Step = 'choose' | 'assign' | 'auto_mode';

export default function SpotOwnerSetupWizard({ open, spots, onDismiss }: Props) {
  const navigate = useNavigate();
  const { t, direction } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isMulti = spots.length > 1;

  const [step, setStep] = useState<Step>(isMulti ? 'assign' : 'choose');
  const [assignments, setAssignments] = useState<Record<string, ReleaseMethod | null>>(
    () => Object.fromEntries(spots.map((s) => [s.id, null])),
  );
  const [singleChoice, setSingleChoice] = useState<ReleaseMethod | null>(null);
  const [autoMode, setAutoMode] = useState<'auto' | 'notify'>('auto');
  const [autoReclaim, setAutoReclaim] = useState(true);
  const [rationaleOpen, setRationaleOpen] = useState(false);
  const [permError, setPermError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const patchMutation = useMutation({
    mutationFn: (patch: Parameters<typeof updateLocationAutomation>[0]) =>
      updateLocationAutomation(patch).then((r) => r.data.data),
    onSuccess: (data) => qc.setQueryData(['location-automation'], data),
  });

  if (!open) return null;

  const dismiss = () => {
    localStorage.setItem('spot_owner_wizard_dismissed', 'true');
    onDismiss();
  };

  const assignMethod = (spotId: string, method: ReleaseMethod) => {
    setAssignments((prev) => ({
      ...prev,
      [spotId]: prev[spotId] === method ? null : method,
    }));
  };

  const selectAllAs = (method: ReleaseMethod) => {
    setAssignments(Object.fromEntries(spots.map((s) => [s.id, method])));
  };

  const getEffectiveAssignments = (): Record<string, ReleaseMethod | null> => {
    if (!isMulti && singleChoice) {
      return { [spots[0].id]: singleChoice };
    }
    return assignments;
  };

  const hasAutoSpots = () => {
    const a = getEffectiveAssignments();
    return Object.values(a).some((m) => m === 'auto');
  };

  const getSpotsByMethod = (method: ReleaseMethod) => {
    const a = getEffectiveAssignments();
    return spots.filter((s) => a[s.id] === method);
  };

  const countByMethod = (method: ReleaseMethod | null) => {
    const a = getEffectiveAssignments();
    return Object.values(a).filter((m) => m === method).length;
  };

  const selectedCount = () => {
    const a = getEffectiveAssignments();
    return Object.values(a).filter((m) => m !== null).length;
  };

  // ── auto-release permission flow ────────────────────────────────────────

  const requestAutoReleasePermission = async () => {
    if (!isNativePlatform()) {
      setStep('auto_mode');
      return;
    }
    setRationaleOpen(true);
  };

  const onRationaleContinue = async () => {
    setRationaleOpen(false);
    setBusy(true);
    setPermError(null);
    try {
      const { state } = await autoReleasePlugin.requestPermissions();
      if (state === 'granted') {
        setStep('auto_mode');
      } else {
        setPermError(t('auto_release_permission_denied'));
      }
    } catch (e) {
      setPermError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const onRationaleDecline = () => {
    setRationaleOpen(false);
    // Remove auto from assignments, fall through to other methods
    if (!isMulti) {
      setSingleChoice(null);
    } else {
      setAssignments((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (next[k] === 'auto') next[k] = null;
        }
        return next;
      });
    }
  };

  const finishAutoRelease = async () => {
    setBusy(true);
    try {
      await patchMutation.mutateAsync({
        releaseEnabled: true,
        releaseMode: autoMode,
        returnAutoReclaim: autoReclaim,
      });

      const b = (user as any)?.building as
        | { id: string; latitude: number | null; longitude: number | null; timezone: string }
        | null;
      if (isNativePlatform() && b?.latitude && b?.longitude) {
        await autoReleasePlugin.setBuildingAnchor({
          buildingId: b.id,
          latitude: b.latitude,
          longitude: b.longitude,
          timezone: b.timezone,
        });
        const settings = await getLocationAutomation().then((r) => r.data.data);
        await autoReleasePlugin.arm({
          releaseHours: settings.releaseHours,
          windowStartLocal: settings.releaseWindowStartLocal ?? '06:00',
          windowEndLocal: settings.releaseWindowEndLocal ?? '09:00',
          apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'https://api.lotngo.com/api/v1',
        });
      }
    } catch {
      // best-effort
    } finally {
      setBusy(false);
    }
  };

  // ── navigation after wizard ─────────────────────────────────────────────

  const navigateToMethod = (method: ReleaseMethod, spotIds: string[]) => {
    if (method === 'schedule' && spotIds.length > 0) {
      navigate(`/spots/${spotIds[0]}/schedule`);
    } else if (method === 'share_now' && spotIds.length > 0) {
      navigate(`/spots/${spotIds[0]}/free`);
    }
  };

  const handleSingleChoice = (method: ReleaseMethod) => {
    setSingleChoice(method);
    if (method === 'auto') {
      requestAutoReleasePermission();
    } else {
      dismiss();
      navigateToMethod(method, [spots[0].id]);
    }
  };

  const handleMultiContinue = () => {
    if (selectedCount() === 0) return;
    if (hasAutoSpots()) {
      requestAutoReleasePermission();
    } else {
      finishAndNavigate();
    }
  };

  const finishAndNavigate = async () => {
    dismiss();
    const scheduleSpots = getSpotsByMethod('schedule');
    const shareNowSpots = getSpotsByMethod('share_now');

    // Navigate to the first actionable method
    if (scheduleSpots.length > 0) {
      navigate(`/spots/${scheduleSpots[0].id}/schedule`);
    } else if (shareNowSpots.length > 0) {
      navigate(`/spots/${shareNowSpots[0].id}/free`);
    }
  };

  const handleAutoModeDone = async () => {
    await finishAutoRelease();
    dismiss();
    const scheduleSpots = getSpotsByMethod('schedule');
    const shareNowSpots = getSpotsByMethod('share_now');
    if (scheduleSpots.length > 0) {
      navigate(`/spots/${scheduleSpots[0].id}/schedule`);
    } else if (shareNowSpots.length > 0) {
      navigate(`/spots/${shareNowSpots[0].id}/free`);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        dir={direction}
        style={overlay}
        onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      >
        <div style={card}>
          {step === 'choose' && !isMulti && (
            <SingleSpotStep
              spot={spots[0]}
              t={t}
              onChoice={handleSingleChoice}
              onSkip={dismiss}
            />
          )}

          {step === 'assign' && isMulti && (
            <AssignStep
              spots={spots}
              assignments={assignments}
              t={t}
              direction={direction}
              onAssign={assignMethod}
              onSelectAll={selectAllAs}
              onContinue={handleMultiContinue}
              onSkip={dismiss}
              selectedCount={selectedCount()}
              countByMethod={countByMethod}
            />
          )}

          {step === 'auto_mode' && (
            <AutoModeStep
              t={t}
              direction={direction}
              autoMode={autoMode}
              autoReclaim={autoReclaim}
              onModeChange={setAutoMode}
              onReclaimChange={setAutoReclaim}
              onDone={handleAutoModeDone}
              busy={busy}
            />
          )}

          {permError && (
            <div style={{ fontSize: '12px', color: '#b91c1c', padding: '0 20px 12px' }}>
              {permError}
            </div>
          )}
        </div>
      </div>

      <AutoReleaseRationaleDialog
        open={rationaleOpen}
        onContinue={onRationaleContinue}
        onDecline={onRationaleDecline}
      />
    </>
  );
}

// ── Step: Single spot — 3 action cards ────────────────────────────────────

function SingleSpotStep({
  spot,
  t,
  onChoice,
  onSkip,
}: {
  spot: SpotInfo;
  t: (k: string) => string;
  onChoice: (m: ReleaseMethod) => void;
  onSkip: () => void;
}) {
  return (
    <>
      <div style={headerArea}>
        <div style={iconCircle}>
          <span style={{ fontSize: '26px' }}>🅿️</span>
        </div>
        <div style={titleText}>{t('wizard_title_single')}</div>
        <div style={subtitleText}>{t('wizard_subtitle_single')}</div>
      </div>

      <div style={cardsArea}>
        <MethodCard
          icon="📅"
          iconBg="#EEEDFE"
          title={t('wizard_schedule_title')}
          desc={t('wizard_schedule_desc')}
          onClick={() => onChoice('schedule')}
        />
        <MethodCard
          icon="⚡"
          iconBg="#E1F5EE"
          title={t('wizard_share_now_title')}
          desc={t('wizard_share_now_desc')}
          onClick={() => onChoice('share_now')}
        />
        <MethodCard
          icon="📍"
          iconBg="#FAECE7"
          title={t('wizard_auto_title')}
          desc={t('wizard_auto_desc')}
          onClick={() => onChoice('auto')}
          highlighted
          badge={t('wizard_recommended')}
        />

        <div style={tipBox}>
          <span style={{ fontSize: '14px', flexShrink: 0 }}>🛡️</span>
          <span style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>
            {t('wizard_tip')}
          </span>
        </div>

        <button onClick={onSkip} style={skipBtn}>{t('wizard_skip')}</button>
      </div>
    </>
  );
}

// ── Step: Multi-spot per-spot assignment ──────────────────────────────────

function AssignStep({
  spots,
  assignments,
  t,
  direction,
  onAssign,
  onSelectAll,
  onContinue,
  onSkip,
  selectedCount,
  countByMethod,
}: {
  spots: SpotInfo[];
  assignments: Record<string, ReleaseMethod | null>;
  t: (k: string) => string;
  direction: string;
  onAssign: (id: string, m: ReleaseMethod) => void;
  onSelectAll: (m: ReleaseMethod) => void;
  onContinue: () => void;
  onSkip: () => void;
  selectedCount: number;
  countByMethod: (m: ReleaseMethod | null) => number;
}) {
  return (
    <>
      <div style={{ ...headerArea, paddingBottom: '8px' }}>
        <div style={iconCircle}>
          <span style={{ fontSize: '26px' }}>🅿️</span>
        </div>
        <div style={titleText}>
          {t('wizard_subtitle_multi').replace('{n}', String(spots.length))}
        </div>
      </div>

      <div style={{ padding: '0 16px 4px' }}>
        <div style={{ ...titleText, fontSize: '15px', textAlign: 'center', marginBottom: '2px' }}>
          {t('wizard_pick_spots_title')}
        </div>
        <div style={{ ...subtitleText, marginBottom: '12px' }}>
          {t('wizard_pick_spots_subtitle')}
        </div>
      </div>

      <div style={cardsArea}>
        {spots.map((spot) => (
          <SpotAssignRow
            key={spot.id}
            spot={spot}
            selected={assignments[spot.id]}
            t={t}
            onAssign={(m) => onAssign(spot.id, m)}
          />
        ))}

        {/* Summary bar */}
        <div style={summaryBar}>
          {countByMethod('schedule') > 0 && (
            <SummaryPill
              color="#534AB7"
              bg="#EEEDFE"
              label={t('wizard_summary_schedule').replace('{n}', String(countByMethod('schedule')))}
            />
          )}
          {countByMethod('share_now') > 0 && (
            <SummaryPill
              color="#0F6E56"
              bg="#E1F5EE"
              label={t('wizard_summary_share_now').replace('{n}', String(countByMethod('share_now')))}
            />
          )}
          {countByMethod('auto') > 0 && (
            <SummaryPill
              color="#993C1D"
              bg="#FAECE7"
              label={t('wizard_summary_auto').replace('{n}', String(countByMethod('auto')))}
            />
          )}
          {countByMethod(null) > 0 && (
            <SummaryPill
              color="#6b7280"
              bg="#f3f4f6"
              label={t('wizard_summary_skipped').replace('{n}', String(countByMethod(null)))}
            />
          )}
        </div>

        <button
          onClick={onContinue}
          disabled={selectedCount === 0}
          style={{
            ...primaryBtn,
            opacity: selectedCount === 0 ? 0.5 : 1,
            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {selectedCount > 0
            ? t('wizard_continue_with_n').replace('{n}', String(selectedCount))
            : t('wizard_no_spots_selected')}
        </button>
        <button onClick={onSkip} style={skipBtn}>{t('wizard_skip')}</button>
      </div>
    </>
  );
}

// ── SpotAssignRow ─────────────────────────────────────────────────────────

function SpotAssignRow({
  spot,
  selected,
  t,
  onAssign,
}: {
  spot: SpotInfo;
  selected: ReleaseMethod | null;
  t: (k: string) => string;
  onAssign: (m: ReleaseMethod) => void;
}) {
  const isSkipped = selected === null;
  return (
    <div style={{ ...spotRow, opacity: isSkipped ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <div style={spotAvatar}>{spot.spot_number}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
            {t(spot.level_code) || spot.level_label} · {spot.spot_number}
          </div>
        </div>
        {isSkipped && (
          <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
            {t('wizard_skipped')}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <MethodToggleBtn
          active={selected === 'schedule'}
          color="#534AB7"
          bg="#EEEDFE"
          label={t('wizard_method_schedule')}
          icon="📅"
          onClick={() => onAssign('schedule')}
        />
        <MethodToggleBtn
          active={selected === 'share_now'}
          color="#0F6E56"
          bg="#E1F5EE"
          label={t('wizard_method_share_now')}
          icon="⚡"
          onClick={() => onAssign('share_now')}
        />
        <MethodToggleBtn
          active={selected === 'auto'}
          color="#993C1D"
          bg="#FAECE7"
          label={t('wizard_method_auto')}
          icon="📍"
          onClick={() => onAssign('auto')}
        />
      </div>
    </div>
  );
}

// ── Step: Auto-release mode choice ────────────────────────────────────────

function AutoModeStep({
  t,
  direction,
  autoMode,
  autoReclaim,
  onModeChange,
  onReclaimChange,
  onDone,
  busy,
}: {
  t: (k: string) => string;
  direction: string;
  autoMode: 'auto' | 'notify';
  autoReclaim: boolean;
  onModeChange: (m: 'auto' | 'notify') => void;
  onReclaimChange: (v: boolean) => void;
  onDone: () => void;
  busy: boolean;
}) {
  return (
    <>
      <div style={headerArea}>
        <div style={{ ...iconCircle, backgroundColor: '#E1F5EE' }}>
          <span style={{ fontSize: '24px' }}>✅</span>
        </div>
        <div style={titleText}>{t('wizard_auto_mode_title')}</div>
        <div style={subtitleText}>{t('wizard_auto_mode_subtitle')}</div>
      </div>

      <div style={cardsArea}>
        {/* Auto mode */}
        <button
          onClick={() => onModeChange('auto')}
          style={{
            ...modeCard,
            borderColor: autoMode === 'auto' ? '#005bc0' : '#e5e7eb',
            borderWidth: autoMode === 'auto' ? '2px' : '1px',
          }}
        >
          {autoMode === 'auto' && (
            <div style={popularBadge}>{t('wizard_auto_mode_popular')}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{
              ...radioOuter,
              borderColor: autoMode === 'auto' ? '#005bc0' : '#d1d5db',
            }}>
              {autoMode === 'auto' && <div style={radioInner} />}
            </div>
            <div style={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
                {t('wizard_auto_mode_auto_title')}
              </div>
              <div style={{ fontSize: '11.5px', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 }}>
                {t('wizard_auto_mode_auto_desc')}
              </div>
            </div>
          </div>
        </button>

        {/* Notify mode */}
        <button
          onClick={() => onModeChange('notify')}
          style={{
            ...modeCard,
            borderColor: autoMode === 'notify' ? '#005bc0' : '#e5e7eb',
            borderWidth: autoMode === 'notify' ? '2px' : '1px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{
              ...radioOuter,
              borderColor: autoMode === 'notify' ? '#005bc0' : '#d1d5db',
            }}>
              {autoMode === 'notify' && <div style={radioInner} />}
            </div>
            <div style={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
                {t('wizard_auto_mode_notify_title')}
              </div>
              <div style={{ fontSize: '11.5px', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 }}>
                {t('wizard_auto_mode_notify_desc')}
              </div>
            </div>
          </div>
        </button>

        {/* Auto-reclaim toggle */}
        <div style={reclaimRow}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>
              {t('wizard_auto_reclaim_title')}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              {t('wizard_auto_reclaim_desc')}
            </div>
          </div>
          <button
            role="switch"
            aria-checked={autoReclaim}
            onClick={() => onReclaimChange(!autoReclaim)}
            style={{
              ...toggleStyle,
              backgroundColor: autoReclaim ? '#16a34a' : '#d1d5db',
            }}
          >
            <span
              style={{
                ...toggleThumb,
                transform: autoReclaim
                  ? direction === 'rtl' ? 'translateX(-20px)' : 'translateX(20px)'
                  : 'translateX(2px)',
              }}
            />
          </button>
        </div>

        <button
          onClick={onDone}
          disabled={busy}
          style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? '…' : t('wizard_done')}
        </button>
      </div>
    </>
  );
}

// ── Shared small components ───────────────────────────────────────────────

function MethodCard({
  icon,
  iconBg,
  title,
  desc,
  onClick,
  highlighted,
  badge,
}: {
  icon: string;
  iconBg: string;
  title: string;
  desc: string;
  onClick: () => void;
  highlighted?: boolean;
  badge?: string;
}) {
  return (
    <button onClick={onClick} style={{
      ...methodCardStyle,
      borderColor: highlighted ? '#005bc0' : '#e5e7eb',
      borderWidth: highlighted ? '2px' : '1px',
    }}>
      {badge && <div style={recommendedBadge}>{badge}</div>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
        <div style={{ ...methodIcon, backgroundColor: iconBg }}>
          <span style={{ fontSize: '18px' }}>{icon}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>{title}</div>
          <div style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: 1.4, marginTop: '2px' }}>{desc}</div>
        </div>
        <span style={{ color: highlighted ? '#005bc0' : '#9ca3af', fontSize: '18px', flexShrink: 0, marginTop: '6px' }}>›</span>
      </div>
    </button>
  );
}

function MethodToggleBtn({
  active,
  color,
  bg,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  color: string;
  bg: string;
  label: string;
  icon: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '8px 4px',
        borderRadius: '8px',
        border: active ? `2px solid ${color}` : '1px solid #e5e7eb',
        background: active ? bg : 'transparent',
        fontSize: '11px',
        fontWeight: 600,
        color: active ? color : '#9ca3af',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        transition: 'all 0.15s',
      }}
    >
      <span style={{ fontSize: '13px' }}>{icon}</span>
      {label}
    </button>
  );
}

function SummaryPill({ color, bg, label }: { color: string; bg: string; label: string }) {
  return (
    <span style={{
      fontSize: '11px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '2px',
        background: bg,
        border: `1px solid ${color}`,
        display: 'inline-block',
      }} />
      <span style={{ color, fontWeight: 600 }}>{label}</span>
    </span>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  padding: '16px',
};

const card: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: '16px',
  maxWidth: '380px',
  width: '100%',
  maxHeight: '85vh',
  overflowY: 'auto',
};

const headerArea: React.CSSProperties = {
  padding: '20px 20px 8px',
  textAlign: 'center',
};

const iconCircle: React.CSSProperties = {
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  backgroundColor: '#e0edff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 10px',
};

const titleText: React.CSSProperties = {
  fontSize: '17px',
  fontWeight: 700,
  color: '#111',
  marginBottom: '4px',
};

const subtitleText: React.CSSProperties = {
  fontSize: '12.5px',
  color: '#6b7280',
  lineHeight: 1.45,
};

const cardsArea: React.CSSProperties = {
  padding: '8px 16px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const methodCardStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: '12px',
  borderStyle: 'solid',
  padding: '12px',
  cursor: 'pointer',
  background: '#fff',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  textAlign: 'start',
};

const methodIcon: React.CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const recommendedBadge: React.CSSProperties = {
  position: 'absolute',
  top: '-9px',
  right: '12px',
  backgroundColor: '#e0edff',
  color: '#005bc0',
  fontSize: '10.5px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '6px',
};

const tipBox: React.CSSProperties = {
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  padding: '8px 10px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
};

const skipBtn: React.CSSProperties = {
  width: '100%',
  padding: '8px',
  border: 'none',
  background: 'transparent',
  color: '#9ca3af',
  fontSize: '12.5px',
  cursor: 'pointer',
};

const primaryBtn: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: '#005bc0',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};

const spotRow: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '12px',
  transition: 'opacity 0.15s',
};

const spotAvatar: React.CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: '#e0edff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: '13px',
  fontWeight: 600,
  color: '#005bc0',
};

const summaryBar: React.CSSProperties = {
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  padding: '8px 12px',
  display: 'flex',
  gap: '12px',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const modeCard: React.CSSProperties = {
  width: '100%',
  borderRadius: '12px',
  borderStyle: 'solid',
  padding: '12px',
  cursor: 'pointer',
  background: '#fff',
  position: 'relative',
  textAlign: 'start',
};

const popularBadge: React.CSSProperties = {
  position: 'absolute',
  top: '-9px',
  right: '12px',
  backgroundColor: '#e0edff',
  color: '#005bc0',
  fontSize: '10.5px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '6px',
};

const radioOuter: React.CSSProperties = {
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  borderStyle: 'solid',
  borderWidth: '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: '2px',
};

const radioInner: React.CSSProperties = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#005bc0',
};

const reclaimRow: React.CSSProperties = {
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  padding: '10px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
};

const toggleStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  width: '44px',
  height: '26px',
  borderRadius: '13px',
  border: 'none',
  flexShrink: 0,
  transition: 'background-color 0.2s',
  padding: 0,
  cursor: 'pointer',
};

const toggleThumb: React.CSSProperties = {
  position: 'absolute',
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  transition: 'transform 0.2s',
};
